import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import toast from '../../utils/toast';
import {
  ListSkeleton,
  LoadError,
  skeletonHeight,
} from '../pages/dashboard/dashboard-shared-components/load-state';
import {
  Presence,
  Staleable,
  StateSwitch,
  listItemMotion,
  m,
} from '../../motion';
import { apiRoute } from '../../constants/api-constants';
import { useAuth } from '../../context/auth-provider';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
} from '../../style-utils/theme';
import { AppButton } from '../button/app-button';

interface SessionRow {
  id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  current: boolean;
}

// Turn a raw UA string into something a human recognises. Best-effort only —
// the UA is attacker-controllable, so this is a label, never a security signal.
const describeDevice = (ua: string | null): string => {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua) && !/Chromium/.test(ua)
    ? 'Chrome'
    : /Safari\//.test(ua) && !/Chrome\//.test(ua)
    ? 'Safari'
    : /Firefox\//.test(ua)
    ? 'Firefox'
    : 'Browser';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iOS/.test(ua)
    ? 'iOS'
    : /Mac OS X|Macintosh/.test(ua)
    ? 'macOS'
    : /Linux/.test(ua)
    ? 'Linux'
    : '';

  return os ? `${browser} on ${os}` : browser;
};

const formatWhen = (iso: string): string => {
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;

  return `${Math.floor(hours / 24)} d ago`;
};

export const SessionsPanel: React.FC = () => {
  const { logout } = useAuth();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const actionRef = useRef(false);
  const requestRef = useRef(0);

  const load = () => {
    const request = ++requestRef.current;
    setLoading(true);
    setLoadError(null);
    axios
      .get(`${apiRoute}/auth/sessions`)
      .then(res => {
        if (request === requestRef.current) {
          setRows(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {
        if (request === requestRef.current) {
          /* NOT setRows([]). This is the security screen: an empty list here
             renders as "No other active sessions.", so a failed request told
             the reader, in plain words, that nothing else was signed in to
             their account. That is the one lie this panel must never tell. */
          setLoadError(
            'We could not check your signed-in devices. This is not a statement that there are none.'
          );
        }
      })
      .finally(() => {
        if (request === requestRef.current) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    load();

    return () => {
      requestRef.current += 1;
    };
  }, []);

  const revoke = async (row: SessionRow) => {
    if (actionRef.current) {
      return;
    }

    actionRef.current = true;
    setBusy(row.id);
    try {
      await axios.delete(`${apiRoute}/auth/sessions/${row.id}`);
      // Revoking the device you're on is just a logout.
      if (row.current) {
        await logout();

        return;
      }
      toast.success('Device signed out');
      load();
    } catch {
      toast.error('Could not sign that device out.');
    } finally {
      actionRef.current = false;
      setBusy(null);
    }
  };

  const signOutEverywhere = async () => {
    if (actionRef.current) {
      return;
    }

    actionRef.current = true;
    setSigningOutAll(true);
    try {
      await logout(true);
    } finally {
      actionRef.current = false;
      setSigningOutAll(false);
    }
  };

  return (
    <Panel>
      {/* NO HEADING HERE. Account › Devices already renders "Signed-in devices"
          and the same explanatory sentence directly above this panel, and this
          panel has exactly one mount point — so its own copy was a second title
          under the first, saying the same thing twice. */}

      {/* ONE BOX, FOUR FILLS. "Loading devices…" is a skeleton the shape of
          the rows now, and StateSwitch crossfades it, the failure, the empty
          line and the list inside one container with a reserved floor. A
          reload after a revoke is a refresh, not a first load: the list stays
          and dims until the answer lands. */}
      <StateSwitch
        state={
          loading && rows.length === 0
            ? 'loading'
            : loadError
              ? 'error'
              : rows.length === 0
                ? 'empty'
                : 'ready'
        }
        minHeight={skeletonHeight(2, 56)}
      >
        {loading && rows.length === 0 ? (
          <ListSkeleton rows={2} rowHeight={56} label="devices" />
        ) : loadError ? (
          <LoadError message={loadError} onRetry={load} />
        ) : rows.length === 0 ? (
          <Muted>No other active sessions.</Muted>
        ) : (
          <Staleable stale={loading}>
          <List>
          {/* Rows arrive in a cascade and a revoked device animates out. */}
          <Presence>
          {rows.map((row, index) => (
            <Row key={row.id} {...listItemMotion(index)}>
              <DeviceText>
                <DeviceName>
                  {describeDevice(row.user_agent)}
                  {row.current && <CurrentTag>This device</CurrentTag>}
                </DeviceName>
                <Meta>
                  {row.ip ? `${row.ip} · ` : ''}active{' '}
                  {formatWhen(row.last_used_at)}
                </Meta>
              </DeviceText>
              {/* THE DEVICE YOU ARE ON IS A DIFFERENT ACT.
                  `revoke` already knows this and calls logout() for the current
                  row — but the control said "Sign out" in the same ghost
                  treatment as every other row, so the button that ends your
                  session looked exactly like the one that ends somebody else's.
                  The label now names what happens. */}
              <RevokeButton
                variant={row.current ? 'secondary' : 'ghost'}
                loading={busy === row.id}
                disabled={actionRef.current && busy !== row.id}
                loadingLabel={
                  row.current
                    ? 'Signing you out'
                    : `Revoking ${describeDevice(row.user_agent)}`
                }
                onClick={() => revoke(row)}
              >
                {busy === row.id
                  ? 'Signing out…'
                  : row.current
                    ? 'Sign out here'
                    : 'Sign out'}
              </RevokeButton>
            </Row>
          ))}
          </Presence>
          </List>
          </Staleable>
        )}
      </StateSwitch>

      <AllButton
        variant="destructive"
        loading={signingOutAll}
        loadingLabel="Signing out everywhere"
        disabled={Boolean(busy)}
        onClick={signOutEverywhere}
      >
        Sign out everywhere
      </AllButton>
    </Panel>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
`;



const Muted = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${palette.slateLight};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* A motion row: the list spreads listItemMotion(index) on it.
   WRAPS. At 320px "Sign out here" beside the device name and the "This device"
   tag needed 23px more than the row had, and the button would not shrink, so
   the whole account card scrolled sideways (measured 2026-09-11). Now the
   button drops under the text when it cannot fit, still on the right. */
const Row = m.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.xs};
  background: ${palette.irisTintSoft};
  border-radius: ${radius.sm};
`;

const DeviceName = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-size: 14px;
  font-weight: 600;
  color: ${palette.ink};
`;

const CurrentTag = styled.span`
  padding: ${spacing.half} ${spacing.xs};
  font-size: ${typography.size.xxs}px;
  font-weight: 600;
  color: ${palette.irisDark};
  background: ${palette.irisTint};
  border-radius: ${radius.pill};
`;

const Meta = styled.div`
  margin-top: ${spacing.half};
  font-size: 12px;
  color: ${palette.slate};
`;

/* The text column may shrink; min-width 0 lets a long device name wrap. */
const DeviceText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RevokeButton = styled(AppButton)`
  flex-shrink: 0;
  margin-left: auto;
`;

const AllButton = styled(AppButton)`
  margin-top: ${spacing.xs};
`;
